import snakeize from 'snakeize';
import getFullName from 'utils/getFullName';
import intersection from 'lodash/intersection';
import {featureFlags} from 'utils/const';
import {Data} from '@store/@types/util';
import {TeamId} from 'records/TeamId';
import {DepartmentId} from 'records/Department';
import camelize from 'camelize';

/**
 * Fields used from old userList endpoint
 * TODO Remove usages
 * @link AdminWelcomeEmailSource
 * @link AdminWelcomeEmailStore
 */
const userListFetchSchema = {
  last_login_date: undefined,
  last_emailed: undefined,
  confirmed: undefined,
  reminders_sent: undefined,
  has_set_password: undefined,
};

/**
 * Fields unique to the auth response
 */
const authUserSchema = {
  permissions: undefined,
  flags: undefined,
  accountName: undefined,
  solutionList: undefined,
  tenantId: undefined,
  i360Embed: undefined,
  globalUserId: undefined,
};

// Schema that comes from dashboard end point
const dashboardUserSchema = {
  id: undefined,
  userId: undefined,
  accountId: undefined,
  teamId: undefined,
  teamName: undefined,
  firstName: undefined,
  lastName: undefined,
  avatar: undefined,
  color: undefined,
  role: undefined,
  email: undefined,
  active: undefined,
  hidden: undefined,
  departmentId: undefined,
  departmentName: undefined,
  timezone: undefined,
  isTeamManager: undefined,
  isCoach: undefined,
  managerId: undefined,
  slackEmail: undefined,
  slackId: undefined,
  jobId: undefined,
  display_name: undefined,
  displayName: undefined,
  languages: undefined,
  botAutoJoinExclusion: undefined,
};

export const userSchema = {
  ...userListFetchSchema,
  ...authUserSchema,
  ...dashboardUserSchema,
};

export enum Role {
  USER = 'user',
  MANAGER = 'manager',
  ADMIN = 'admin',
}

export enum MediaflySolution {
  Coach360 = 'Coach360',
  Intelligence360 = 'Intelligence360',
  Engagement360 = 'Engagement360',
  Revenue360 = 'Revenue360',
  Value360 = 'Value360',
}

export type UserId = string;
export type AccountId = string;

interface UserInterface {
  readonly id: UserId;
  readonly userId: UserId;
  readonly accountId: AccountId;
  readonly teamId: TeamId;
  readonly teamName: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly avatar: string;
  readonly color: string;
  readonly role: Role;
  readonly email: string;
  readonly active: boolean;
  readonly hidden: boolean;
  readonly departmentId: DepartmentId;
  readonly departmentName: DepartmentId;
  readonly timezone: string;
  readonly isTeamManager: boolean;
  readonly isCoach: boolean;
  readonly managerId: UserId;
  readonly slackEmail: string;
  readonly slackId: string;
  readonly jobId: string;
  readonly languages: string[];
  readonly botAutoJoinExclusion: boolean;

  // Auth User only
  readonly flags: featureFlags[];
  readonly permissions: string[];
  readonly accountName: string;
  // Mediafly SSO
  readonly solutionList?: MediaflySolution[];
  readonly tenantId?: string;
  readonly i360Embed: boolean;
  readonly globalUserId: string;
  // @deprecated
  readonly displayName?: string;
}

type UserProps = {
  [key in keyof typeof userSchema]?: any;
};

export class User extends Data<UserInterface>({
  ...snakeize(userSchema),
  ...userSchema,
}) {
  constructor(props: UserProps = {}) {
    super({
      // fields are snaked for legacy code which used that format.
      // replace with camel case variants if you find usages
      ...snakeize(props),
      ...camelize(props),
      // Some endpoints return userId rather than id
      id: props.id ?? props.userId,
    });
  }

  getFullName() {
    return getFullName(this);
  }

  /**
   * First Name Last Initial
   * @returns {string}
   */
  getDisplayName() {
    return displayName(this);
  }

  /**
   * Does the user have the manager OR admin role?
   *
   * @returns {boolean}
   */
  isManager() {
    return this.role === 'manager' || this.isAdmin();
  }

  isAdmin() {
    return this.role === 'admin';
  }

  canShareTriggers() {
    const share_permissions = [
      'can_share_triggers_globally',
      'can_share_triggers_with_departments',
      'can_share_triggers_with_teams',
    ];

    return intersection(share_permissions, this.permissions).length > 0;
  }

  isTriggersUser() {
    return isTriggersUser(this);
  }

  get firstNamePossessive() {
    return this.firstName ? `${this.firstName}'s` : 'their';
  }

  canCoach(user) {
    return this.isCoach || this.isTeamManager || this.isAdmin();
  }
}

export function displayName(user) {
  if (user.firstName) {
    return `${user.firstName} ${user.lastName ? user.lastName.substring(0, 1) + '.' : ''}`;
  }
  return `${user.first_name || ''} ${user.last_name ? user.last_name.substring(0, 1) + '.' : ''}`;
}

export function isTriggersUser(user: {firstName: string; lastName: string}) {
  return (
    (user.firstName === 'ExecVision' || user.firstName === 'Coach360') &&
    user.lastName === 'Triggers'
  );
}

export default User;
